'use client'

import Head from 'next/head'
import { useEffect } from 'react'

import { getFaviconMimeType } from '@/lib/branding-icons'
import { useBranding } from '@/hooks/use-branding'

function syncDocumentFaviconLinks(icons: ReturnType<typeof useBranding>['iconSet']) {
  if (typeof document === 'undefined') return

  const upsertLink = (rel: string, href: string, options?: { sizes?: string; type?: string }) => {
    const selector = options?.sizes
      ? `link[rel="${rel}"][sizes="${options.sizes}"]`
      : `link[rel="${rel}"]:not([sizes])`
    let link = document.querySelector<HTMLLinkElement>(selector)

    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', rel)
      if (options?.sizes) link.setAttribute('sizes', options.sizes)
      document.head.appendChild(link)
    }

    link.setAttribute('href', href)
    if (options?.type) link.setAttribute('type', options.type)
  }

  upsertLink('icon', icons.ico, { type: getFaviconMimeType(icons.ico) })
  upsertLink('icon', icons.png32, { sizes: '32x32', type: getFaviconMimeType(icons.png32) })
  upsertLink('icon', icons.png16, { sizes: '16x16', type: getFaviconMimeType(icons.png16) })
  upsertLink('apple-touch-icon', icons.appleTouchIcon, { sizes: '180x180' })
}

/**
 * Keeps favicon in sync when tenant customization loads client-side.
 * `_app` Head only sees getInitialProps config, which often lacks mediaDefaults.
 */
export function TenantFaviconHead() {
  const { iconSet } = useBranding()

  useEffect(() => {
    syncDocumentFaviconLinks(iconSet)
  }, [iconSet])

  const faviconIcoType = getFaviconMimeType(iconSet.ico)

  return (
    <Head>
      <link rel="icon" href={iconSet.ico} type={faviconIcoType} key="tenant-favicon-ico" />
      <link rel="icon" type={getFaviconMimeType(iconSet.png32)} sizes="32x32" href={iconSet.png32} key="tenant-favicon-32" />
      <link rel="icon" type={getFaviconMimeType(iconSet.png16)} sizes="16x16" href={iconSet.png16} key="tenant-favicon-16" />
      <link rel="apple-touch-icon" sizes="180x180" href={iconSet.appleTouchIcon} key="tenant-apple-touch-icon" />
      <link rel="manifest" href={iconSet.manifest} key="tenant-web-manifest" />
    </Head>
  )
}
